import { createConsumer } from '@rails/actioncable';
import { workerTimeout } from './workerTimeout';
import { addHashToUrl } from '../../widget/helpers/utils';
import { BUS_EVENTS } from '../constants/busEvents';
import { emitter } from 'shared/helpers/mitt';

const PRESENCE_INTERVAL = 20000;
const RECONNECT_INTERVAL = 3000;

class BaseActionCableConnector {
  static isDisconnected = false;

  worker = null;

  constructor(app, pubsubToken, websocketHost = '', isWidget = false) {
    const fixedWebsocketURL = isWidget
      ? addHashToUrl(websocketHost)
      : websocketHost;
    const websocketURL = fixedWebsocketURL
      ? `${fixedWebsocketURL}/cable`
      : undefined;

    this.consumer = createConsumer(websocketURL);
    this.subscription = this.consumer.subscriptions.create(
      {
        channel: 'RoomChannel',
        pubsub_token: pubsubToken,
        account_id: app.$store.getters.getCurrentAccountId,
        user_id: app.$store.getters.getCurrentUserID,
      },
      {
        rejected() {
          this.initReconnectTimer();
        },
        updatePresence() {
          this.perform('update_presence');
        },
        received: this.onReceived,
        disconnected: () => {
          BaseActionCableConnector.isDisconnected = true;
          this.onDisconnected();
          this.initReconnectTimer();
          emitter.emit(BUS_EVENTS.WEBSOCKET_DISCONNECT);
        },
      }
    );
    this.app = app;
    this.events = {};
    this.reconnectTimer = null;
    this.isAValidEvent = () => true;
    if (window.Worker) {
      const blob = new Blob([workerTimeout(PRESENCE_INTERVAL)], {
        type: 'application/javascript',
      });
      this.worker = new Worker(URL.createObjectURL(blob));
    }
    this.triggerPresenceInterval = () => {
      if (this.worker) {
        this.worker.postMessage('start');
        this.worker.onmessage = () => {
          this.worker.postMessage('start');
          this.subscription.updatePresence();
        };
      } else {
        setTimeout(() => {
          this.subscription.updatePresence();
          this.triggerPresenceInterval();
        }, PRESENCE_INTERVAL);
      }
    };
    this.triggerPresenceInterval();
  }

  checkConnection() {
    const isConnectionActive = this.consumer.connection.isOpen();
    if (!isConnectionActive) {
      this.connect();
    }
    const shouldReconnect =
      BaseActionCableConnector.isDisconnected && isConnectionActive;

    if (shouldReconnect) {
      this.clearReconnectTimer();
      this.onReconnect();
      BaseActionCableConnector.isDisconnected = false;
      emitter.emit(BUS_EVENTS.WEBSOCKET_RECONNECT_COMPLETED);
    } else {
      this.initReconnectTimer();
    }
  }

  clearReconnectTimer = () => {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  };

  initReconnectTimer = () => {
    this.clearReconnectTimer();
    this.reconnectTimer = setTimeout(() => {
      this.checkConnection();
    }, RECONNECT_INTERVAL);
  };

  onReconnect = () => {};

  onDisconnected = () => {};

  disconnect() {
    this.consumer.disconnect();
  }

  connect() {
    this.consumer.connect();
  }

  onReceived = ({ event, data } = {}) => {
    if (this.isAValidEvent(data)) {
      if (this.events[event] && typeof this.events[event] === 'function') {
        this.events[event](data);
      }
    }
  };
}

export default BaseActionCableConnector;
